// WizardTriggerButton — minimal additive trigger for wizard launches from
// list pages without mutating the page composition primitives or generated
// configs (per brief constraint).
//
// Renders a fixed-position floating action button (bottom-right) styled with
// the v2 brand-primary token. Permission gating is a placeholder prop — real
// resolution lands in ε CHG-1170.

import { Plus } from "lucide-react";

export interface WizardTriggerButtonProps {
  /** Display label shown next to the icon (e.g., "New campaign"). */
  label: string;
  /** Click handler — typically `wizard.open` from useWizard. */
  onClick: () => void;
  /** Permission code expected by the wizard manifest. Stored as a data
   *  attribute for QA; real gating defers to ε CHG-1170. */
  requirePermission?: string;
  /** Optional override of the default test id. */
  testId?: string;
}

export function WizardTriggerButton({
  label,
  onClick,
  requirePermission,
  testId,
}: WizardTriggerButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId ?? "wizard-trigger"}
      data-require-permission={requirePermission}
      style={{
        position: "fixed",
        bottom: 28,
        right: 28,
        zIndex: 40,
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        height: 44,
        padding: "0 18px 0 14px",
        borderRadius: 9999,
        border: "none",
        background: "var(--color-primary)",
        color: "white",
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
        boxShadow: "0 8px 24px -8px rgba(15, 23, 42, 0.35)",
        transition: "background 0.12s ease, transform 0.12s ease",
      }}
    >
      <Plus size={18} aria-hidden="true" />
      {label}
    </button>
  );
}
